/**
 * useCanConfirmDecisions (FLOW-672)
 *
 * Whether the caller may confirm a decision in this project — the frontend
 * mirror of `require_project_owner_admin` on
 * `POST /projects/{id}/decisions/{id}/confirm`.
 *
 * The predicate itself lives in `utils/permissions`; what this hook adds is
 * resolving its two inputs, which no single call returns. The project role
 * comes from the members list, and the org role must be read for *this
 * project's* tenant rather than `AuthMeResponse.tenant_role`, which reports
 * only the caller's primary tenant — see `tenantRoleFor`.
 *
 * Two surfaces review decisions (`DecisionsPage` and `CapturedDecisionsPanel`
 * via `PlanPage`), which is why the plumbing lives here rather than in either.
 * Fails closed: while the lookups are in flight, or if either fails, the
 * answer is `false` and the Confirm control stays hidden. Hiding a control a
 * privileged user could have used is recoverable by reloading; showing one
 * that 403s is not.
 */

import { Accessor, createMemo, createResource } from "solid-js";
import { getProject, listMembers } from "../api/projects";
import type { ProjectId } from "../api/types";
import { currentUser } from "../stores/currentUserStore";
import {
  canConfirmDecisions,
  getUserRole,
  tenantRoleFor,
} from "../utils/permissions";

export function useCanConfirmDecisions(
  projectId: Accessor<ProjectId>,
): Accessor<boolean> {
  const [project] = createResource(projectId, (id) =>
    getProject(id).catch(() => null),
  );
  const [members] = createResource(projectId, (id) =>
    listMembers(id).catch(() => null),
  );

  return createMemo(() =>
    canConfirmDecisions(
      getUserRole(currentUser()?.id, members()?.members ?? []),
      tenantRoleFor(currentUser()?.tenants, project()?.tenant_id),
    ),
  );
}
